import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Collapse, Card, Button, Checkbox, Input, Select, Tag, Empty, Spin, Alert, Typography, Row, Col, Space } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useGridData } from '../context/GridDataContext'
import { getStateOptions } from '../utils/stateHelpers'
import { isLookupValid, buildLookupQuery, shapeLookupResults } from '../utils/overrideLookup'
import { fetchOverrideLookup } from '../../../store/reducers/feeManagement/feeManagementSlice'
import '../styles/OverrideLookupSimulator.scss'

const { Panel } = Collapse
const { Text, Title } = Typography

const DATA_TYPE_OPTIONS = [
  { label: 'Fee', value: 'fee' },
  { label: 'Due Date', value: 'due_date' },
  { label: 'Rush Due Date', value: 'rush_due_date' },
]
const DATA_TYPE_LABELS = { fee: 'Fee', due_date: 'Due Date', rush_due_date: 'Rush Due Date' }

// A standard (light) single-select for the simulator form. Plain antd Select —
// not GridSelect — so it doesn't pick up the dark AG-Grid styling.
const SimSelect = (props) => (
  <Select showSearch allowClear optionFilterProp="label" style={{ width: '100%' }} {...props} />
)

const OverrideLookupSimulator = () => {
  const dispatch = useDispatch()
  const { mappingData, isLoadingMappingData } = useGridData()
  const { data, isLoading, error } = useSelector((state) => state.feeManagement.overrideLookup)
  const [hasRun, setHasRun] = useState(false)

  const [productId, setProductId] = useState(undefined)
  const [branchId, setBranchId] = useState(undefined)
  const [vendorId, setVendorId] = useState(undefined)
  const [anyVendor, setAnyVendor] = useState(false)
  const [stateCode, setStateCode] = useState(undefined)
  const [countyFips, setCountyFips] = useState(undefined)
  const [zip, setZip] = useState('')
  const [dataTypes, setDataTypes] = useState(['fee', 'due_date', 'rush_due_date'])

  const productOptions = useMemo(
    () => (mappingData.products || []).map((p) => ({ value: p.id, label: p.description })),
    [mappingData.products],
  )
  const branchOptions = useMemo(
    () => (mappingData.branches || []).map((b) => ({ value: b.id, label: b.name })),
    [mappingData.branches],
  )
  const vendorOptions = useMemo(
    () => (mappingData.panelists || []).map((v) => ({ value: v.id, label: v.name })),
    [mappingData.panelists],
  )
  // getStateOptions tags each option with a `searchTerms` array; antd Select
  // would spread that unknown key onto the DOM node. We search by label
  // (which already contains both the code and name), so drop the extra field.
  const stateOptions = useMemo(
    () => getStateOptions(mappingData.state_and_counties_map).map(({ value, label }) => ({ value, label })),
    [mappingData.state_and_counties_map],
  )
  const countyOptions = useMemo(() => {
    const counties = mappingData.state_and_counties_map?.[stateCode]?.counties
    if (!counties) { return [] }
    return Object.values(counties)
      .map((c) => ({ value: c.fips_code, label: c.readable }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [mappingData.state_and_counties_map, stateCode])

  // Resolve the selected state key to its FIPS code (what the engine matches on).
  const stateFips = stateCode ? mappingData.state_and_counties_map?.[stateCode]?.fips_code : undefined

  const params = {
    productIds: productId ? [productId] : [],
    branchIds: branchId ? [branchId] : [],
    panelistId: anyVendor ? undefined : vendorId,
    anyPanelists: anyVendor,
    state: stateFips,
    county: countyFips,
    zip: zip.trim() || undefined,
    dataTypes,
  }
  const canRun = isLookupValid(params) && !isLoading

  const onStateChange = (value) => {
    setStateCode(value)
    setCountyFips(undefined) // county depends on state
  }

  const onSimulate = () => {
    setHasRun(true)
    dispatch(fetchOverrideLookup(buildLookupQuery(params)))
  }

  const results = useMemo(() => shapeLookupResults(data, mappingData), [data, mappingData])

  const renderOverride = (o) => (
    <div className={`lookup-result-override${o.isWinner ? ' is-winner' : ''}`} key={o.id}>
      <div className="lookup-result-values">
        {Object.entries(o.overrideData).map(([type, val]) => (
          <Tag color={o.isWinner ? 'green' : 'default'} key={type}>
            {DATA_TYPE_LABELS[type] || type}: {String(val)}
          </Tag>
        ))}
        {o.isWinner && <Tag color="blue">Applied</Tag>}
      </div>
      <Text type="secondary" className="lookup-result-meta">
        Sheet: {o.sheetName}
        {o.branchNames.length > 0 && ` · Branches: ${o.branchNames.join(', ')}`}
        {o.locationType && ` · ${o.locationType}: ${[...o.states, ...o.counties, ...o.zips].join(', ')}`}
      </Text>
    </div>
  )

  const renderResults = () => {
    if (isLoading) { return <Spin /> }
    if (error) { return <Alert type="error" message={error} showIcon /> }
    if (!hasRun) { return null }
    if (results.length === 0) {
      return <Empty description="No overrides apply for these inputs — the standard (product default) fee would be used." />
    }
    return results.map((row) => (
      <Card size="small" className="lookup-result-product" key={row.productId} title={row.productName}>
        {row.overrides.length === 0 ? (
          <Empty description="No overrides apply — standard fee." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            {renderOverride(row.overrides[0])}
            {row.overrides.length > 1 && (
              <Collapse ghost>
                <Panel header={`Show all ${row.overrides.length} matches (priority order)`} key="all">
                  {row.overrides.slice(1).map(renderOverride)}
                </Panel>
              </Collapse>
            )}
          </>
        )}
      </Card>
    ))
  }

  return (
    <div className="override-lookup-simulator" data-testid="override-lookup-simulator">
      <Collapse ghost>
        <Panel
          header={
            <Space size="small">
              <SearchOutlined />
              <Title level={5} style={{ margin: 0 }}>
              Override Lookup Simulator
              </Title>
              <Text type="secondary">Test which overrides apply for a hypothetical order</Text>
            </Space>
          }
          key="simulator"
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <label>Product *</label>
              <SimSelect
                options={productOptions}
                value={productId}
                onChange={setProductId}
                placeholder="Select a product"
                loading={isLoadingMappingData}
                data-testid="lookup-product-select"
              />
            </Col>
            <Col xs={24} md={8}>
              <label>Branch</label>
              <SimSelect
                options={branchOptions}
                value={branchId}
                onChange={setBranchId}
                placeholder="Select a branch (parents auto-included)"
                loading={isLoadingMappingData}
                data-testid="lookup-branch-select"
              />
            </Col>
            <Col xs={24} md={8}>
              <label>Vendor</label>
              <SimSelect
                options={vendorOptions}
                value={vendorId}
                onChange={setVendorId}
                placeholder="Select a vendor"
                disabled={anyVendor}
                loading={isLoadingMappingData}
                data-testid="lookup-vendor-select"
              />
              <Checkbox checked={anyVendor} onChange={(e) => setAnyVendor(e.target.checked)} className="lookup-any-vendor">
                Any vendor
              </Checkbox>
            </Col>
            <Col xs={24} md={8}>
              <label>State</label>
              <SimSelect
                options={stateOptions}
                value={stateCode}
                onChange={onStateChange}
                placeholder="Select a state"
                data-testid="lookup-state-select"
              />
            </Col>
            <Col xs={24} md={8}>
              <label>County</label>
              <SimSelect
                options={countyOptions}
                value={countyFips}
                onChange={setCountyFips}
                placeholder={stateCode ? 'Select a county' : 'Select a state first'}
                disabled={!stateCode}
                data-testid="lookup-county-select"
              />
            </Col>
            <Col xs={24} md={8}>
              <label>Zip</label>
              <Input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder={stateCode ? 'Enter a zip' : 'Select a state first'}
                disabled={!stateCode}
                data-testid="lookup-zip-input"
              />
            </Col>
            <Col xs={24}>
              <label>Data types</label>
              <Checkbox.Group
                className="lookup-data-types"
                options={DATA_TYPE_OPTIONS}
                value={dataTypes}
                onChange={setDataTypes}
              />
            </Col>
          </Row>

          <div className="lookup-actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={onSimulate} disabled={!canRun} data-testid="lookup-simulate-button">
            Simulate Lookup
            </Button>
            {!isLookupValid(params) && (
              <Text type="secondary" className="lookup-hint">
              Pick a product and at least one of: branch, vendor (or “any vendor”), or state.
              </Text>
            )}
          </div>

          <div className="lookup-results" data-testid="lookup-results">
            {renderResults()}
          </div>
        </Panel>
      </Collapse>
    </div>
  )
}

export default OverrideLookupSimulator
